<template>
    <div class="ctf px-1 pt-2">
        <div class="text-caption text--secondary mb-3">
            Minutos que um professor ou turma leva para ir de uma aula à seguinte, conforme a posição dos dois ambientes no campus.
            Os valores iniciais são o padrão do sistema; ajuste para a realidade deste campus.
        </div>

        <div v-for="field in fields" :key="field.key" class="ctf-row d-flex align-center py-2">
            <v-icon small class="mr-3 text--secondary">{{ field.icon }}</v-icon>
            <div class="flex-grow-1 pr-4">
                <div class="text-body-2">{{ field.label }}</div>
                <div class="text-caption text--secondary">{{ field.hint }}</div>
            </div>
            <v-text-field
                class="ctf-input flex-grow-0"
                type="number"
                min="0"
                max="600"
                :value="valueOf(field.key)"
                :error-messages="errors[field.key]"
                suffix="min"
                outlined
                dense
                hide-details="auto"
                @input="(v) => update(field.key, v)"
            />
        </div>

        <div class="text-caption text--secondary mt-3">
            O intervalo entre os horários do catálogo e o recreio contam como folga automaticamente.
            Pares com tempo próprio ficam em Tempo entre locais.
        </div>
    </div>
</template>

<script>
import { watch } from 'vue'

// Padrão global do sistema (mesmos valores do worker: common/ambience/travel.py).
const SYSTEM_DEFAULTS = {
    travel_same_block_minutes: 0,
    travel_same_place_minutes: 3,
    travel_cross_place_minutes: 10,
    travel_tolerance_minutes: 5,
}

/**
 * Aba "Deslocamento" do cadastro de Campus: os quatro tempos padrão (em
 * minutos) que a alocação de ambientes usa para julgar se uma troca de sala
 * entre aulas seguidas é viável. Campo vazio no campus é preenchido com o
 * padrão do sistema ao abrir, para o usuário ver e ajustar o número em vez
 * de adivinhar. Emite `input` com o model inteiro atualizado; quem persiste
 * é o Campus.vue (o model vira o corpo do PUT).
 */
export default {
    name: 'SysRegisterCampusTravelFields',
    props: {
        value: { type: Object, default: () => ({}) },
        errors: { type: Object, default: () => ({}) },
    },
    emits: ['input'],
    setup(props, { emit }) {
        const fields = [
            { key: 'travel_same_block_minutes', icon: 'mdi-door', label: 'Mesmo bloco', hint: 'Trocar de sala dentro do mesmo bloco' },
            { key: 'travel_same_place_minutes', icon: 'mdi-domain', label: 'Mesmo prédio, blocos diferentes', hint: 'Ir de um bloco a outro do mesmo prédio' },
            { key: 'travel_cross_place_minutes', icon: 'mdi-office-building', label: 'Prédios diferentes', hint: 'Ir de um prédio a outro do campus' },
            { key: 'travel_tolerance_minutes', icon: 'mdi-timer-sand', label: 'Tolerância entre aulas coladas', hint: 'Deslocamento aceito mesmo sem intervalo entre os horários' },
        ]

        const isEmpty = (v) => v === null || v === undefined || v === ''

        const valueOf = (key) => {
            const v = props.value ? props.value[key] : null
            return isEmpty(v) ? '' : v
        }

        const update = (key, raw) => {
            const n = isEmpty(raw) ? null : Number.parseInt(raw, 10)
            emit('input', { ...(props.value || {}), [key]: Number.isFinite(n) ? n : null })
        }

        // Preenche com o padrão do sistema o que o campus ainda não tem.
        const prefill = () => {
            const current = props.value || {}
            const missing = Object.entries(SYSTEM_DEFAULTS).filter(([k]) => isEmpty(current[k]))
            if (!missing.length) return
            emit('input', { ...current, ...Object.fromEntries(missing) })
        }

        watch(() => (props.value ? props.value.id : null), prefill, { immediate: true })

        return { fields, valueOf, update }
    },
}
</script>

<style scoped>
.ctf-row + .ctf-row {
    border-top: 1px solid rgba(0, 0, 0, 0.06);
}
.ctf-input {
    width: 120px;
}
.ctf-input >>> input {
    text-align: right;
}
</style>
